import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { AlertTriangle, CheckCircle2, FileCheck, Search } from 'lucide-react'
import { toast } from 'sonner'
import api from '@/services/api'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { getFriendlyError } from '@/utils/getFriendlyError'
import { buildEmpresaPatchFromCnpjLookup } from '@/utils/empresaCnpjLookup'
import { maskPhone } from '@/components/PjAgrada/pjAgradaFormat'
import type { Deal } from '@/types'
import { cnpjMask, onlyDigits } from '../utils'
import {
  buildContractDataForm,
  emptyContractDataForm,
  getMissingContractDataFields,
  getMissingHandoverCompletionFields,
  isContractDataComplete,
  isHandoverCompletionComplete,
  type ContractDataForm,
  type ContractDataModalMode,
  type ContractDeliveryMode,
} from '../contractDataUtils'
import { DatetimeLocalField } from '../DatetimeLocalField'
import {
  checkContractDataConflictsForDeal,
  loadContractDataFormForDeal,
  persistContractDataForDeal,
  resolveEmpresaIdForDeal,
  type ContractDataConflict,
} from '../contractDataFlow'

const inputClass =
  'w-full rounded-md border border-[var(--glass-border)] bg-[var(--glass-bg)] px-2.5 py-1.5 text-sm font-sans text-[var(--text-primary)] placeholder:text-[var(--text-muted)] placeholder:font-sans outline-none focus:border-accent'

const monoInputClass = `${inputClass} font-mono tracking-wide placeholder:font-mono`

const labelClass = 'text-[10px] font-medium uppercase tracking-wider text-[var(--font-color-label)]'

type ContractDataModalProps = {
  open: boolean
  mode?: ContractDataModalMode
  deal: Deal
  onClose: () => void
  onConfirm: (form: ContractDataForm) => void | Promise<void>
}

function formatFormForInputs(form: ContractDataForm): ContractDataForm {
  return {
    ...form,
    cnpj: form.cnpj ? cnpjMask(form.cnpj) : '',
    telefone: form.telefone ? maskPhone(form.telefone) : '',
  }
}

function FormSection({
  title,
  children,
  className,
}: {
  title: string
  children: ReactNode
  className?: string
}) {
  return (
    <section
      className={`rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)]/50 p-3 ${className ?? ''}`}
    >
      <h4 className="mb-2 text-[10px] font-bold uppercase tracking-wider text-[var(--font-color-muted)]">
        {title}
      </h4>
      {children}
    </section>
  )
}

const conflictInputClass =
  'border-[var(--color-danger,#e5484d)] focus:border-[var(--color-danger,#e5484d)]'

function Field({
  label,
  children,
  className,
  conflict,
}: {
  label: string
  children: ReactNode
  className?: string
  conflict?: string
}) {
  return (
    <div className={`flex flex-col gap-1 ${className ?? ''}`}>
      <label className={labelClass}>{label}</label>
      {children}
      {conflict ? (
        <p className="text-[11px] leading-snug text-[var(--color-danger,#e5484d)]">{conflict}</p>
      ) : null}
    </div>
  )
}

export function ContractDataModal({
  open,
  mode = 'gerar_contrato',
  deal,
  onClose,
  onConfirm,
}: ContractDataModalProps) {
  const [form, setForm] = useState<ContractDataForm>(emptyContractDataForm())
  const [loading, setLoading] = useState(false)
  const [searchingCnpj, setSearchingCnpj] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [resolvedEmpresaId, setResolvedEmpresaId] = useState<string | null>(deal.empresaId || null)
  const [conflicts, setConflicts] = useState<ContractDataConflict[]>([])
  const [checkingConflicts, setCheckingConflicts] = useState(false)

  const isHandoverMode = mode === 'handover_complete'

  const conflictByField = useMemo(() => {
    const map: Partial<Record<ContractDataConflict['field'], string>> = {}
    for (const conflict of conflicts) {
      map[conflict.field] = conflict.message
    }
    return map
  }, [conflicts])

  useEffect(() => {
    if (!open) return

    let cancelled = false
    setLoading(true)
    setForm(emptyContractDataForm())
    setResolvedEmpresaId(deal.empresaId || null)
    setConflicts([])

    void (async () => {
      try {
        const [loadedForm, empresaId] = await Promise.all([
          loadContractDataFormForDeal(deal),
          resolveEmpresaIdForDeal(deal),
        ])
        if (cancelled) return
        setForm(formatFormForInputs(loadedForm))
        setResolvedEmpresaId(empresaId)
      } catch {
        if (!cancelled) {
          setForm(formatFormForInputs(buildContractDataForm(deal)))
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [open, deal])

  useEffect(() => {
    if (!open || loading || isHandoverMode) return

    let cancelled = false
    const timer = window.setTimeout(() => {
      setCheckingConflicts(true)
      void checkContractDataConflictsForDeal(deal, form, resolvedEmpresaId)
        .then((result) => {
          if (!cancelled) setConflicts(result)
        })
        .catch(() => {
          if (!cancelled) setConflicts([])
        })
        .finally(() => {
          if (!cancelled) setCheckingConflicts(false)
        })
    }, 450)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [
    open,
    loading,
    deal,
    resolvedEmpresaId,
    form.cnpj,
    form.responsavelAssinaturaEmail,
    form.emailFinanceiro,
    form.telefone,
  ])

  const setField = <K extends keyof ContractDataForm>(key: K, value: ContractDataForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const handleCnpjSearch = async () => {
    const rawCnpj = onlyDigits(form.cnpj)
    if (rawCnpj.length !== 14) {
      toast.error('CNPJ inválido — precisa ter 14 dígitos')
      return
    }

    const empresaId = resolvedEmpresaId ?? (await resolveEmpresaIdForDeal(deal))
    if (!empresaId) {
      toast.error('Deal sem empresa vinculada — não foi possível salvar os dados do CNPJ')
      return
    }

    setSearchingCnpj(true)
    try {
      const { data } = await api.get(`/empresas/cnpj/${rawCnpj}?source=brasilapi`)
      const empresa = data.data ?? data
      if (!empresa) {
        toast.error('CNPJ não encontrado')
        return
      }

      const patchPayload = buildEmpresaPatchFromCnpjLookup(empresa, rawCnpj, deal.empresaNome)
      await api.patch(`/empresas/${empresaId}`, patchPayload)
      setResolvedEmpresaId(empresaId)

      setForm((prev) => ({
        ...prev,
        cnpj: cnpjMask(patchPayload.cnpj ?? rawCnpj),
        telefone: prev.telefone || (patchPayload.telefone ? maskPhone(patchPayload.telefone) : ''),
        responsavelAssinaturaEmail:
          prev.responsavelAssinaturaEmail || patchPayload.email || prev.responsavelAssinaturaEmail,
        emailFinanceiro: prev.emailFinanceiro || patchPayload.email || prev.emailFinanceiro,
      }))

      toast.success('Dados do CNPJ consultados e salvos na empresa')
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 404) {
        toast.error('CNPJ não encontrado')
      } else {
        toast.error(getFriendlyError(err, 'Erro ao buscar CNPJ'))
      }
    } finally {
      setSearchingCnpj(false)
    }
  }

  const handleSubmit = async () => {
    const missing = isHandoverMode
      ? getMissingHandoverCompletionFields(form)
      : getMissingContractDataFields(form)
    if (missing.length > 0) {
      toast.error(`Preencha: ${missing.join(', ')}`)
      return
    }

    if (!isHandoverMode) {
      const latestConflicts = await checkContractDataConflictsForDeal(deal, form, resolvedEmpresaId)
      setConflicts(latestConflicts)
      if (latestConflicts.length > 0) {
        toast.error('Corrija os dados já cadastrados em outra conta antes de continuar')
        return
      }
    }

    setSubmitting(true)
    try {
      if (!isHandoverMode) {
        await persistContractDataForDeal(deal, form)
      }
      await onConfirm(form)
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao salvar dados do contrato'))
    } finally {
      setSubmitting(false)
    }
  }

  const missingFields = isHandoverMode
    ? getMissingHandoverCompletionFields(form)
    : getMissingContractDataFields(form)
  const isComplete = isHandoverMode ? isHandoverCompletionComplete(form) : isContractDataComplete(form)
  const hasConflicts = !isHandoverMode && conflicts.length > 0

  const modalCopy = isHandoverMode
    ? {
        title: 'Concluir handover',
        description:
          'Registre quando o negócio foi fechado e descreva o projeto vendido para onboarding e financeiro.',
        confirmLabel: 'Concluir handover',
        confirmIcon: <CheckCircle2 size={14} />,
      }
    : {
        title: 'Dados para o contrato',
        description: 'Complete os dados antes de avançar para Gerar Contrato.',
        confirmLabel: 'Salvar e continuar',
        confirmIcon: <FileCheck size={14} />,
      }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={modalCopy.title}
      description={modalCopy.description}
      size="xl"
      className="max-w-[722px]"
      stackPriority="high"
      footer={
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={submitting} className="flex-1">
            Cancelar
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={modalCopy.confirmIcon}
            loading={submitting}
            disabled={loading || !isComplete || hasConflicts || (!isHandoverMode && checkingConflicts)}
            onClick={() => void handleSubmit()}
            className="flex-1"
          >
            {modalCopy.confirmLabel}
          </Button>
        </div>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center gap-2 py-8 text-sm text-text-label">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-accent/30 border-t-accent" />
          Carregando dados...
        </div>
      ) : (
        <div className="space-y-3">
          {hasConflicts && (
            <div
              className="flex items-start gap-2 rounded-md px-3 py-2"
              style={{
                border: '1px solid color-mix(in srgb, var(--color-danger) 40%, transparent)',
                background: 'color-mix(in srgb, var(--color-danger) 12%, var(--bg-card))',
              }}
            >
              <AlertTriangle
                size={15}
                className="mt-0.5 shrink-0"
                style={{ color: 'var(--color-danger)' }}
                aria-hidden
              />
              <div className="space-y-1">
                <p className="text-xs font-medium leading-snug text-[var(--text-primary)]">
                  Dados já cadastrados em outra conta:
                </p>
                <ul className="list-disc space-y-0.5 pl-4 text-xs text-[var(--text-primary)]">
                  {conflicts.map((conflict) => (
                    <li key={`${conflict.field}-${conflict.message}`}>{conflict.message}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {!isComplete && (
            <div
              className="flex items-start gap-2 rounded-md px-3 py-2"
              style={{
                border: '1px solid color-mix(in srgb, var(--color-warning) 40%, transparent)',
                background: 'color-mix(in srgb, var(--color-warning) 14%, var(--bg-card))',
              }}
            >
              <AlertTriangle
                size={15}
                className="mt-0.5 shrink-0"
                style={{ color: 'var(--color-warning)' }}
                aria-hidden
              />
              <p className="text-xs font-medium leading-snug text-[var(--text-primary)]">
                Faltam: {missingFields.join(' · ')}
              </p>
            </div>
          )}

          {isHandoverMode ? (
            <FormSection title="Conclusão do handover">
              <p className="mb-3 text-xs leading-relaxed text-[var(--font-color-muted)]">
                Os dados do contrato já foram registrados nas etapas anteriores. Informe apenas o
                fechamento e o resumo do projeto para o pós-venda.
              </p>
              <div className="space-y-3">
                <Field label="Data e hora do fechamento">
                  <DatetimeLocalField
                    value={form.dataFechamentoHandover}
                    onChange={(value) => setField('dataFechamentoHandover', value)}
                    placeholder="Data e hora do ganho"
                  />
                </Field>
                <Field label="Observação do projeto vendido">
                  <textarea
                    value={form.observacoesHandover}
                    onChange={(e) => setField('observacoesHandover', e.target.value)}
                    disabled={submitting}
                    rows={5}
                    placeholder="Detalhe escopo, serviços contratados, prazos e condições acordadas..."
                    className={`${inputClass} resize-none`}
                  />
                </Field>
              </div>
            </FormSection>
          ) : (
          <>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <FormSection title="Assinatura do contrato">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Field label="Nome completo">
                  <input
                    type="text"
                    value={form.responsavelAssinaturaNome}
                    onChange={(e) => setField('responsavelAssinaturaNome', e.target.value)}
                    placeholder="Quem assina"
                    className={inputClass}
                  />
                </Field>
                <Field label="E-mail" conflict={conflictByField.responsavelAssinaturaEmail}>
                  <input
                    type="email"
                    value={form.responsavelAssinaturaEmail}
                    onChange={(e) => setField('responsavelAssinaturaEmail', e.target.value)}
                    placeholder="assinante@empresa.com"
                    className={`${inputClass} ${conflictByField.responsavelAssinaturaEmail ? conflictInputClass : ''}`}
                  />
                </Field>
              </div>
            </FormSection>

            <FormSection title="Empresa">
              <Field label="CNPJ" conflict={conflictByField.cnpj}>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={form.cnpj}
                    onChange={(e) => setField('cnpj', cnpjMask(e.target.value))}
                    placeholder="00.000.000/0000-00"
                    className={`${monoInputClass} ${conflictByField.cnpj ? conflictInputClass : ''}`}
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    icon={<Search size={13} />}
                    loading={searchingCnpj}
                    onClick={() => void handleCnpjSearch()}
                    className="shrink-0 px-2.5"
                  >
                    Buscar
                  </Button>
                </div>
              </Field>
            </FormSection>

            <FormSection title="Contato">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Field label="Nome">
                  <input
                    type="text"
                    value={form.contatoNome}
                    onChange={(e) => setField('contatoNome', e.target.value)}
                    placeholder="Contato principal"
                    className={inputClass}
                  />
                </Field>
                <Field label="WhatsApp" conflict={conflictByField.telefone}>
                  <input
                    type="tel"
                    value={form.telefone}
                    onChange={(e) => setField('telefone', maskPhone(e.target.value))}
                    placeholder="(00) 00000-0000"
                    className={`${inputClass} ${conflictByField.telefone ? conflictInputClass : ''}`}
                  />
                </Field>
              </div>
            </FormSection>

            <FormSection title="Contato financeiro">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Field label="Nome">
                  <input
                    type="text"
                    value={form.responsavelFinanceiroNome}
                    onChange={(e) => setField('responsavelFinanceiroNome', e.target.value)}
                    placeholder="Responsável financeiro"
                    className={inputClass}
                  />
                </Field>
                <Field label="E-mail" conflict={conflictByField.emailFinanceiro}>
                  <input
                    type="email"
                    value={form.emailFinanceiro}
                    onChange={(e) => setField('emailFinanceiro', e.target.value)}
                    placeholder="financeiro@empresa.com"
                    className={`${inputClass} ${conflictByField.emailFinanceiro ? conflictInputClass : ''}`}
                  />
                </Field>
              </div>
            </FormSection>

            <FormSection title="Datas do contrato">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Field label="Data inicial do contrato">
                  <input
                    type="date"
                    value={form.contractStartDate}
                    onChange={(e) => setField('contractStartDate', e.target.value)}
                    className={inputClass}
                  />
                </Field>
                <Field label="Data inicial de pagamento">
                  <input
                    type="date"
                    value={form.firstPaymentDate}
                    onChange={(e) => setField('firstPaymentDate', e.target.value)}
                    className={inputClass}
                  />
                </Field>
              </div>
            </FormSection>
          </div>

          <FormSection title="Formato do contrato">
            <p className="mb-2 text-xs text-[var(--font-color-muted)]">
              Escolha como o financeiro deve entregar o contrato ao cliente (uma opção).
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {(
                [
                  { value: 'pdf' as ContractDeliveryMode, label: 'Gerar PDF do contrato' },
                  { value: 'link' as ContractDeliveryMode, label: 'Gerar link do contrato' },
                ] as const
              ).map((option) => {
                const selected = form.contractDeliveryMode === option.value
                return (
                  <label
                    key={option.value}
                    className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2.5 text-sm transition-colors ${
                      selected
                        ? 'border-accent bg-accent/10 text-[var(--text-primary)]'
                        : 'border-[var(--glass-border)] bg-[var(--glass-bg)]/50 text-[var(--text-primary)] hover:border-accent/40'
                    }`}
                  >
                    <input
                      type="radio"
                      name="contractDeliveryMode"
                      value={option.value}
                      checked={selected}
                      onChange={() => setField('contractDeliveryMode', option.value)}
                      className="accent-accent"
                    />
                    <span className="font-medium">{option.label}</span>
                  </label>
                )
              })}
            </div>
          </FormSection>
          </>
          )}
        </div>
      )}
    </Modal>
  )
}
